import { memo } from 'react';
import classNames from 'classnames';

import { trans } from '@spotahome/soyuz/client';

import { getDateAsUTC } from '../../utils/dateUtils';

import FlexibleOptions from '../AvailabilityFilter/FlexibleOptions';
import DateFilterHeader from '../DateFilterHeader';

import DatePicker from './DatePicker';

import styles from './DateRangeFilter.module.scss';

const noop = () => {};

const DATE_PICKER_THEME = 'modern';

const datesInputStyles = {
  from: 'ga-detail-datepicker-from',
  to: 'ga-detail-datepicker-to'
};

const DateRangeFilterMobile = ({
  from,
  to,
  minDate,
  minStay,
  maxStay,
  onDatesChange,
  hideInput,
  onFlexibleOptionsChange,
  dateRangeOffset,
  showFlexibleOptions = true,
  keepMoveOutCalendarOpen = false,
  onClickBack = null,
  calendarClassnames = null,
  disableDayFnMoveIn = noop,
  disableDayFnMoveOut = noop,
  onFilterSwitcherClick = null
}) => {
  const handleChange = ({ moveIn, moveOut }) => {
    if (moveIn && moveOut) {
      onDatesChange({
        moveIn,
        moveOut
      });
    } else if (moveIn) {
      onDatesChange({
        moveIn
      });
    } else if (moveOut) {
      onDatesChange({
        moveOut
      });
    }

    // Have both move in and move out and they are different from the current values, and if we are NOT in a prefilling of the fields done by our logic instead of the users,  go back
    if (
      moveIn &&
      moveOut &&
      (moveIn !== from || moveOut !== to) &&
      onClickBack &&
      from &&
      to
    ) {
      onClickBack();
    }
  };

  const datesI18n = {
    dateInPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateOutPlaceholder: trans('property_show.book_now_card.placeholder'),
    dateInLabel: trans('property_show.book_now_card.date_in'),
    dateOutLabel: trans('property_show.book_now_card.date_out')
  };

  const inputContainerClasses = classNames(
    styles['date-range-filter__input-container'],
    {
      [styles['date-range-filter__input-container--hidden']]: hideInput
    }
  );

  return (
    <DatePicker
      settings={{
        minDate: getDateAsUTC(minDate) || getDateAsUTC(new Date()),
        minStay,
        maxStay,
        moveInterval: minStay
      }}
      showFromDate
      fromDate={from}
      toDate={to}
      onChange={handleChange}
      styles={datesInputStyles}
      i18n={datesI18n}
      theme={DATE_PICKER_THEME}
      initiallyOpen
      autoCompleteDates
      absolutePosition={false}
      onMoveInClose={() => {}}
      onMoveOutClose={() => {}}
      numberOfMonths={24}
      calendarBordered={false}
      showRanges
      closeOnSelect={false}
      hideOnBlur={false}
      showCalendarArrows={false}
      keepMoveOutCalendarOpen={keepMoveOutCalendarOpen}
      disableDayFnMoveIn={disableDayFnMoveIn}
      disableDayFnMoveOut={disableDayFnMoveOut}
    >
      {({
        renderMoveInInput,
        renderMoveOutInput,
        renderMoveInCalendar,
        renderMoveOutCalendar
      }) => (
        <div className={styles['date-range-filter']}>
          <DateFilterHeader
            isMonthsFilter={false}
            onClickSwitcher={onFilterSwitcherClick}
            subtitle={
              <>
                {minStay ? (
                  <p>
                    {trans('search.exposedfilters.dates.range.subtitle', {
                      nDays: minStay
                    })}
                  </p>
                ) : null}
                {maxStay ? (
                  <p>
                    {trans(
                      'search.exposedfilters.dates.range.subtitle.max_stay',
                      {
                        nDays: maxStay
                      }
                    )}
                  </p>
                ) : null}
              </>
            }
            renderContent={() => (
              <>
                <div className={inputContainerClasses}>
                  {renderMoveInInput()}
                  {renderMoveOutInput()}
                </div>
                {showFlexibleOptions && (
                  <FlexibleOptions
                    onChange={onFlexibleOptionsChange}
                    value={dateRangeOffset}
                    className={styles['date-range-filter__flexible-options']}
                  />
                )}
              </>
            )}
            onClickBack={onClickBack}
          />
          <div
            className={classNames(
              styles['date-range-filter__calendar-container'],
              calendarClassnames
            )}
          >
            {renderMoveInCalendar()}
            {renderMoveOutCalendar()}
          </div>
        </div>
      )}
    </DatePicker>
  );
};

export default memo(DateRangeFilterMobile, (prevProps, nextProps) => {
  return prevProps.from === nextProps.from && prevProps.to === nextProps.to;
});
